// Facebook Conversion API Tracking Utility
// Uses centralized Supabase Edge Function for server-side tracking

const WEBHOOK_URL = "https://dkzjwubycqhrochtaxiv.supabase.co/functions/v1/facebook-capi";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRremp3dWJ5Y3Focm9jaHRheGl2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU4ODU0NDIsImV4cCI6MjA3MTQ2MTQ0Mn0.oQCrb09STfZtcrsN5u3FtObJz0T-5Pg7wJh0CDGku7c";

interface UserDetails {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
}

interface CustomData {
  value?: number;
  currency?: string;
  content_name?: string;
  [key: string]: any;
}

// Extend Window interface for Facebook Pixel
declare global {
  interface Window {
    fbq: (
      action: string,
      eventName: string,
      params?: object,
      options?: { eventID: string }
    ) => void;
  }
}

// Get Facebook cookies for better matching
function getFacebookCookies() {
  const cookies = document.cookie.split(';');
  let fbc = '';
  let fbp = '';
  
  cookies.forEach(cookie => {
    const [name, value] = cookie.trim().split('=');
    if (name === '_fbc') fbc = value;
    if (name === '_fbp') fbp = value;
  });
  
  return { fbc, fbp };
}

/**
 * Generate a safe UUID that works on all browsers including older mobile WebViews
 */
function safeRandomUUID(): string {
  try {
    // Prefer native crypto.randomUUID if available
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to fallback
  }
  
  // Fallback: use crypto.getRandomValues if available
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      // Set version (4) and variant bits
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    // Fall through to final fallback
  }
  
  // Final fallback: timestamp + Math.random
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Track Facebook event with both client-side Pixel and server-side Conversion API
 * This function is designed to never throw - all errors are caught and logged.
 * @param eventName - Facebook event name (InitiateCheckout, Lead, Schedule, Purchase, etc.)
 * @param userDetails - User information (email, phone, name, etc.)
 * @param customData - Additional event data (value, currency, content_name, etc.)
 */
export async function trackFacebookEvent(
  eventName: string,
  userDetails: UserDetails = {},
  customData: CustomData = {}
): Promise<void> {
  try {
    // Generate unique event ID for deduplication between Pixel and CAPI
    const eventId = safeRandomUUID();
    const eventTime = Math.floor(Date.now() / 1000);
    
    // Get Facebook cookies for better matching
    let fbc = '';
    let fbp = '';
    try {
      const cookies = getFacebookCookies();
      fbc = cookies.fbc;
      fbp = cookies.fbp;
    } catch {
      // Ignore cookie access errors
    }
    
    // 1. Fire client-side Facebook Pixel (immediate, browser-based)
    if (typeof window !== 'undefined' && window.fbq) {
      try {
        window.fbq('track', eventName, customData, { eventID: eventId });
        console.log('✅ Facebook Pixel fired:', eventName);
      } catch (error) {
        console.error('❌ Facebook Pixel error:', error);
      }
    } else {
      console.warn('⚠️ Facebook Pixel not loaded');
    }
    
    // 2. Send to Conversion API via Supabase Edge Function (server-side)
    const payload = {
      event_name: eventName,
      event_time: eventTime,
      event_id: eventId,
      event_source_url: typeof window !== 'undefined' ? window.location.href : '',
      action_source: "website",
      user_data: {
        client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        em: userDetails.email,
        ph: userDetails.phone,
        fn: userDetails.firstName,
        ln: userDetails.lastName,
        external_id: userDetails.userId,
        fbc: fbc || undefined,
        fbp: fbp || undefined,
      },
      custom_data: customData,
    };
    
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${SUPABASE_ANON_KEY}` 
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        console.log('✅ Facebook CAPI sent:', eventName);
      } else {
        const errorText = await response.text();
        console.error('❌ Facebook CAPI failed:', errorText);
      }
    } catch (error) {
      console.error('❌ Facebook CAPI network error:', error);
    }
  } catch (error) {
    // Top-level catch: this function should NEVER throw to callers
    console.error('❌ trackFacebookEvent unexpected error:', error);
  }
}
